import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

/**
 * One canned reply a host (or eventually a guest) can insert into the
 * /inbox composer with a single tap. Stored per-user in localStorage.
 */
export interface IQuickReply {
  id: string;
  label: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

const STORAGE_PREFIX = 'cnt-quick-replies';
const GUEST_KEY = `${STORAGE_PREFIX}-guest`;
const IDB_PREFIX = 'quick-replies';
const GUEST_IDB_KEY = `${IDB_PREFIX}:guest`;

/** Starter set seeded on first use so new hosts get value without setup. */
const DEFAULT_REPLIES: Pick<IQuickReply, 'label' | 'body'>[] = [
  { label: 'Approve dates',     body: 'Those dates work — please confirm and I\'ll lock them in.' },
  { label: 'Dates unavailable', body: 'Sorry, those dates aren\'t available. Could you try another window?' },
  { label: 'Gate code',         body: 'Here\'s the gate code: ____. Let me know once you\'re in.' },
  { label: 'Arriving tomorrow?',body: 'Just checking — still arriving tomorrow? Anything I can prep ahead of time?' },
  { label: 'Send rig photo',    body: 'Could you send a quick photo of your rig so I can confirm fit?' },
  { label: 'See you soon',      body: 'Thanks — see you soon! Reach out if anything comes up before arrival.' },
];

function makeId(): string {
  return `qr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

@Injectable({ providedIn: 'root' })
export class QuickReplyService {
  private platformId = inject(PLATFORM_ID);
  private auth = inject(AuthService);

  private readonly store = createIdbKvStore<IQuickReply[]>({
    legacyKeys: { [GUEST_IDB_KEY]: GUEST_KEY },
  });

  private readonly _replies$ = new BehaviorSubject<IQuickReply[]>([]);
  readonly replies$: Observable<IQuickReply[]> = this._replies$.asObservable();

  private storageKey = GUEST_IDB_KEY;

  constructor() {
    // Gate the first refreshKey()/hydrate() on init() completing — otherwise
    // an empty pre-hydration cache would look like "no data yet" and
    // hydrate() would re-seed the default replies over real persisted ones.
    void this.store.init().then(() => {
      this.refreshKey();
      this.auth.currentUser$.subscribe(() => this.refreshKey());
      this.auth.loggedOut$.subscribe(() => this.wipeCurrentKey());
      this.hydrate();
    });
  }

  list(): IQuickReply[] { return this._replies$.value; }

  add(label: string, body: string): IQuickReply {
    const now = new Date().toISOString();
    const next: IQuickReply = { id: makeId(), label: label.trim(), body: body.trim(), createdAt: now, updatedAt: now };
    this.write([...this._replies$.value, next]);
    return next;
  }

  update(id: string, patch: Partial<Pick<IQuickReply, 'label' | 'body'>>): IQuickReply | null {
    const all = this._replies$.value.slice();
    const idx = all.findIndex(r => r.id === id);
    if (idx === -1) return null;
    const updated: IQuickReply = {
      ...all[idx],
      label: patch.label != null ? patch.label.trim() : all[idx].label,
      body:  patch.body  != null ? patch.body.trim()  : all[idx].body,
      updatedAt: new Date().toISOString(),
    };
    all[idx] = updated;
    this.write(all);
    return updated;
  }

  remove(id: string): void {
    this.write(this._replies$.value.filter(r => r.id !== id));
  }

  /** Deletes the outgoing user's own per-email quick replies entirely (rather
   *  than just switching `storageKey` back to guest, which `refreshKey()`'s
   *  `currentUser$` reaction does on its own and leaves the per-email data
   *  orphaned in IDB). Runs on `loggedOut$`, which fires before `currentUser$`
   *  emits `null`, so `this.storageKey` here is still the outgoing user's key. */
  private wipeCurrentKey(): void {
    if (this.storageKey === GUEST_IDB_KEY) return;
    void this.store.delete(this.storageKey);
  }

  /** Swap the storage key on sign-in/out and migrate guest drafts forward.
   *  `store.rekey()`/`migrateLegacyKey()` update the cache synchronously
   *  (before their first internal `await`), so `hydrate()` below already
   *  sees the result even though this method itself stays synchronous. */
  private refreshKey(): void {
    const email = this.auth.currentUser?.email;
    const newKey = email ? `${IDB_PREFIX}:${email}` : GUEST_IDB_KEY;
    if (newKey === this.storageKey) return;
    if (email && isPlatformBrowser(this.platformId)) {
      // A pre-existing per-email localStorage bucket (this user customized
      // replies before this migration) takes priority — migrateLegacyKey()
      // updates the cache synchronously (before its own first await) if it
      // finds one, so the immediately-following rekey() call's cache.has()
      // check already sees it and skips the lower-priority guest fallback.
      void this.store.migrateLegacyKey(newKey, `${STORAGE_PREFIX}-${email}`);
      void this.store.rekey(GUEST_IDB_KEY, newKey);
    }
    this.storageKey = newKey;
    this.hydrate();
  }

  /** Read storage; seed the default set the first time the bucket is empty. */
  private hydrate(): void {
    const existing = this.read();
    if (existing.length > 0) {
      this._replies$.next(existing);
      return;
    }
    const now = new Date().toISOString();
    const seeded: IQuickReply[] = DEFAULT_REPLIES.map((d, i) => ({
      id: makeId(),
      label: d.label,
      body: d.body,
      // Stagger createdAt so chips render in the curated order, not by tie.
      createdAt: new Date(Date.parse(now) + i).toISOString(),
      updatedAt: now,
    }));
    this.write(seeded);
  }

  private read(): IQuickReply[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    return this.store.read(this.storageKey) ?? [];
  }

  private write(items: IQuickReply[]): void {
    if (isPlatformBrowser(this.platformId)) {
      void this.store.write(this.storageKey, items);
    }
    this._replies$.next(items);
  }
}
